import React, { useState, useEffect, useMemo } from "react";
import Card from "../common/Card";
import Icon from "../common/Icon";
import NumericRangeFilter from "../common/NumericRangeFilter";
import SerpFeaturesFilter from "../common/SerpFeaturesFilter";
import DomainFilter from "../common/DomainFilter";
import TrendSparkline from "./TrendSparkline";
import Tooltip from "./Tooltip";
import SerpFeatureIcons from "./SerpFeatureIcons";
import { formatNumber } from "../../utils/formatters";
import { METRIC_TOOLTIPS, PAGINATION, SESSION_KEYS } from "../../utils/constants";
import { exportToCSV } from "../../utils/exportUtils";
import { sanitizeDomainList } from "../../utils/domainFilters";
import useSessionList from "../../hooks/useSessionList";
import "./KeywordInsights.css";

const MAX_ADDRESS_DISPLAY_LENGTH = 28;

const truncateAddress = (address) => {
  if (!address) return '';
  if (address.length <= MAX_ADDRESS_DISPLAY_LENGTH) return address;
  return address.slice(0, MAX_ADDRESS_DISPLAY_LENGTH - 1) + '…';
};

const formatAddressForExport = (address) => {
  if (!address) return '-';
  return address.replace(/,\s*/g, ' - ');
};

const isInRange = (value, min, max) => {
  if (min !== null && (value === null || value === undefined || value < min)) return false;
  if (max !== null && (value === null || value === undefined || value > max)) return false;
  return true;
};

const positionMatches = (position, range) => {
  if (range.min === null && range.max === null) return true;
  const effective = position && position <= 100 ? position : null;
  return isInRange(effective, range.min, range.max);
};

const serpFeaturesMatch = (kwFeatures, selectedFeatures) => {
  if (!selectedFeatures || selectedFeatures.length === 0) return true;
  if (!Array.isArray(kwFeatures) || kwFeatures.length === 0) return false;
  return selectedFeatures.some((f) => kwFeatures.includes(f));
};

const EMPTY_RANGE = { min: null, max: null };

const cleanUrl = (url) => {
  if (!url) return '-';
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    params.delete('srsltid');
    const cleanedSearch = params.toString();
    let cleanedUrl = `${urlObj.protocol}//${urlObj.host}${decodeURIComponent(urlObj.pathname)}`;
    if (cleanedSearch) {
      cleanedUrl += `?${cleanedSearch}`;
    }
    return cleanedUrl;
  } catch (error) {
    return url;
  }
};

const buildKeywordDomainRows = (keywords, selectedDomains, clientDomain) => {
  const rows = [];
  const clientDomainLower = clientDomain ? clientDomain.toLowerCase() : '';
  keywords.forEach((kw) => {
    const entries = Array.isArray(kw.domainPositions) ? kw.domainPositions : [];
    selectedDomains.forEach((domain) => {
      const target = domain.toLowerCase();
      const match = entries.find((entry) => entry.domain && entry.domain.toLowerCase() === target);
      if (!match) return;
      rows.push({
        rowKey: `${kw.keyword}|${kw.formattedAddress || ''}|${domain}`,
        keyword: kw.keyword,
        category: kw.category,
        formattedAddress: kw.formattedAddress,
        volume: kw.volume,
        historicalMetrics: kw.historicalMetrics,
        serpFeatures: kw.serpFeatures,
        domain,
        isClient: target === clientDomainLower,
        position: match.position,
        url: match.url,
        estimatedTraffic: match.estimatedTraffic,
      });
    });
  });
  return rows;
};

const compareRowsByDefault = (a, b) => {
  const volumeDiff = (b.volume || 0) - (a.volume || 0);
  if (volumeDiff !== 0) return volumeDiff;
  const keywordCompare = a.keyword.localeCompare(b.keyword);
  if (keywordCompare !== 0) return keywordCompare;
  const addressCompare = (a.formattedAddress || '').localeCompare(b.formattedAddress || '');
  if (addressCompare !== 0) return addressCompare;
  if (a.isClient && !b.isClient) return -1;
  if (b.isClient && !a.isClient) return 1;
  return a.domain.localeCompare(b.domain);
};

const compareNumericNullsLast = (aValue, bValue, direction) => {
  const aMissing = aValue === null || aValue === undefined;
  const bMissing = bValue === null || bValue === undefined;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return direction === "asc" ? aValue - bValue : bValue - aValue;
};

const getPositionColor = (position) => {
  if (!position || position > 100) return "var(--color-gray-400)";
  if (position <= 3) return "var(--color-success)";
  if (position <= 10) return "var(--color-primary)";
  if (position <= 20) return "var(--color-warning)";
  return "var(--color-gray-600)";
};

const buildDefaultSelectedDomains = (clientDomain, availableDomains) => {
  if (clientDomain && availableDomains.includes(clientDomain)) {
    return [clientDomain];
  }
  return [];
};

const KeywordInsights = ({ keywords, clientDomain, availableDomains = [] }) => {
  const defaultSelectedDomains = buildDefaultSelectedDomains(clientDomain, availableDomains);
  const [selectedDomains, setSelectedDomains] = useSessionList(
    SESSION_KEYS.KEYWORD_SELECTED_DOMAINS,
    defaultSelectedDomains
  );
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [positionRange, setPositionRange] = useState(EMPTY_RANGE);
  const [volumeRange, setVolumeRange] = useState(EMPTY_RANGE);
  const [trafficRange, setTrafficRange] = useState(EMPTY_RANGE);
  const [selectedSerpFeatures, setSelectedSerpFeatures] = useState([]);
  const pageSize = PAGINATION.KEYWORDS_PER_PAGE || 100;

  useEffect(() => {
    setSelectedDomains((previousSelected) => {
      const stillAvailable = sanitizeDomainList(previousSelected, availableDomains);
      if (stillAvailable === previousSelected) return previousSelected;
      return buildDefaultSelectedDomains(clientDomain, availableDomains);
    });
  }, [availableDomains, clientDomain, setSelectedDomains]);

  const hasCategories = useMemo(
    () => keywords?.some((kw) => kw.category && kw.category.trim() !== '') ?? false,
    [keywords]
  );
  const hasLocations = useMemo(
    () => keywords?.some((kw) => kw.formattedAddress && kw.formattedAddress.trim() !== '') ?? false,
    [keywords]
  );

  const excludedDomainsForFilter = useMemo(
    () => availableDomains.filter((d) => !selectedDomains.includes(d)),
    [availableDomains, selectedDomains]
  );

  const handleDomainFilterChange = (newExcluded) => {
    const newSelected = availableDomains.filter((d) => !newExcluded.includes(d));
    setSelectedDomains(newSelected);
    setCurrentPage(1);
  };

  const allRows = useMemo(
    () => buildKeywordDomainRows(keywords || [], selectedDomains, clientDomain),
    [keywords, selectedDomains, clientDomain]
  );

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return allRows
      .filter((row) => {
        if (!term) return true;
        if (row.keyword?.toLowerCase().includes(term)) return true;
        if (row.url?.toLowerCase().includes(term)) return true;
        if (row.domain?.toLowerCase().includes(term)) return true;
        if (row.formattedAddress?.toLowerCase().includes(term)) return true;
        return false;
      })
      .filter((row) => positionMatches(row.position, positionRange))
      .filter((row) => isInRange(row.volume ?? 0, volumeRange.min, volumeRange.max))
      .filter((row) => isInRange(row.estimatedTraffic, trafficRange.min, trafficRange.max))
      .filter((row) => serpFeaturesMatch(row.serpFeatures, selectedSerpFeatures));
  }, [allRows, searchTerm, positionRange, volumeRange, trafficRange, selectedSerpFeatures]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    if (!sortConfig.key) {
      copy.sort(compareRowsByDefault);
      return copy;
    }
    copy.sort((a, b) => {
      if (sortConfig.key === "keyword") {
        const cmp = a.keyword.localeCompare(b.keyword);
        if (cmp !== 0) return sortConfig.direction === "asc" ? cmp : -cmp;
        const addressCmp = (a.formattedAddress || '').localeCompare(b.formattedAddress || '');
        return sortConfig.direction === "asc" ? addressCmp : -addressCmp;
      }
      if (sortConfig.key === "domain") {
        const cmp = a.domain.localeCompare(b.domain);
        return sortConfig.direction === "asc" ? cmp : -cmp;
      }
      if (sortConfig.key === "volume") {
        return compareNumericNullsLast(a.volume, b.volume, sortConfig.direction);
      }
      if (sortConfig.key === "position") {
        const aEff = a.position && a.position <= 100 ? a.position : null;
        const bEff = b.position && b.position <= 100 ? b.position : null;
        return compareNumericNullsLast(aEff, bEff, sortConfig.direction);
      }
      if (sortConfig.key === "estimatedTraffic") {
        return compareNumericNullsLast(a.estimatedTraffic, b.estimatedTraffic, sortConfig.direction);
      }
      return 0;
    });
    return copy;
  }, [filteredRows, sortConfig]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "ChevronsUpDown";
    return sortConfig.direction === "asc" ? "ChevronUp" : "ChevronDown";
  };

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedRows = sortedRows.slice(startIndex, startIndex + pageSize);

  const handleExportCSV = () => {
    const exportData = sortedRows.map((row) => {
      const serpFeaturesText = (row.serpFeatures && row.serpFeatures.length > 0)
        ? row.serpFeatures.join(', ')
        : '-';
      let baseRow = {
        'Mot-clé': row.keyword,
        'Volume': row.volume || 0,
        'Domaine': row.domain,
        'Client': row.isClient ? 'Oui' : 'Non',
        'Position': row.position && row.position <= 100 ? row.position : '-',
        'URL': row.url ? cleanUrl(row.url) : '-',
        'Trafic estimé': row.estimatedTraffic ?? 0,
        'SERP features': serpFeaturesText,
      };
      if (hasLocations) {
        baseRow = { 'Adresse': formatAddressForExport(row.formattedAddress), ...baseRow };
      }
      if (hasCategories) {
        baseRow = { 'Catégorie': row.category || '-', ...baseRow };
      }
      return baseRow;
    });
    exportToCSV(exportData, `mots-cles-${new Date().toISOString().split('T')[0]}`);
  };

  const handlePositionRangeChange = (range) => { setPositionRange(range); setCurrentPage(1); };
  const handleVolumeRangeChange = (range) => { setVolumeRange(range); setCurrentPage(1); };
  const handleTrafficRangeChange = (range) => { setTrafficRange(range); setCurrentPage(1); };
  const handleSerpFeaturesChange = (features) => { setSelectedSerpFeatures(features); setCurrentPage(1); };

  const hasActiveRangeFilters =
    positionRange.min !== null || positionRange.max !== null ||
    volumeRange.min !== null || volumeRange.max !== null ||
    trafficRange.min !== null || trafficRange.max !== null ||
    selectedSerpFeatures.length > 0;

  const handleResetAllFilters = () => {
    setPositionRange(EMPTY_RANGE);
    setVolumeRange(EMPTY_RANGE);
    setTrafficRange(EMPTY_RANGE);
    setSelectedSerpFeatures([]);
    setSearchTerm("");
    setCurrentPage(1);
  };

  if (!keywords || keywords.length === 0) {
    return (
      <Card className="keyword-insights">
        <h3 className="table-title">Analyse des mots-clés</h3>
        <p className="table-empty">Aucune donnée disponible</p>
      </Card>
    );
  }

  return (
    <Card className="keyword-insights">
      <div className="table-header-with-actions">
        <h3 className="table-title">Analyse des mots-clés</h3>
        <div className="header-actions">
          <div className="search-container">
            <Icon name="Search" size={16} className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher un mot-clé, URL, domaine ou adresse..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => { setSearchTerm(""); setCurrentPage(1); }}
              >
                <Icon name="X" size={14} />
              </button>
            )}
          </div>
          <button onClick={handleExportCSV} className="export-button">
            <Icon name="Download" size={16} />
            Exporter CSV
          </button>
        </div>
      </div>

      <div className="keyword-filters-bar">
        <span className="keyword-filters-label">Filtres :</span>
        <DomainFilter
          domains={availableDomains}
          excludedDomains={excludedDomainsForFilter}
          clientDomain={clientDomain}
          onChange={handleDomainFilterChange}
          displayMode="selection"
        />
        <NumericRangeFilter
          label="Position"
          min={positionRange.min}
          max={positionRange.max}
          placeholderMin="1"
          placeholderMax="100"
          step={1}
          onChange={handlePositionRangeChange}
        />
        <NumericRangeFilter
          label="Volume"
          min={volumeRange.min}
          max={volumeRange.max}
          placeholderMin="0"
          placeholderMax="100000"
          step={100}
          formatter={formatNumber}
          onChange={handleVolumeRangeChange}
        />
        <NumericRangeFilter
          label="Trafic estimé"
          min={trafficRange.min}
          max={trafficRange.max}
          placeholderMin="0"
          placeholderMax="50000"
          step={10}
          formatter={formatNumber}
          onChange={handleTrafficRangeChange}
        />
        <SerpFeaturesFilter
          keywords={keywords}
          selected={selectedSerpFeatures}
          onChange={handleSerpFeaturesChange}
        />
        {(hasActiveRangeFilters || searchTerm) && (
          <button
            className="keyword-filters-reset"
            onClick={handleResetAllFilters}
            type="button"
          >
            <Icon name="RotateCcw" size={14} />
            Réinitialiser
          </button>
        )}
        <span className="keyword-filters-count">
          {sortedRows.length} ligne{sortedRows.length > 1 ? 's' : ''}
        </span>
      </div>

      {selectedDomains.length === 0 ? (
        <p className="table-empty">Sélectionne au moins un domaine pour afficher les mots-clés.</p>
      ) : (
        <>
          <div className="table-container">
            <table className="insights-table">
              <thead>
                <tr>
                  {hasCategories && (
                    <th>
                      <Tooltip text="Catégorie associée au mot-clé">
                        <div className="th-content">Catégorie</div>
                      </Tooltip>
                    </th>
                  )}
                  {hasLocations && (
                    <th>
                      <Tooltip text="Adresse géocodée appliquée au scraping de ce mot-clé">
                        <div className="th-content">Adresse</div>
                      </Tooltip>
                    </th>
                  )}
                  <th>
                    <Tooltip text={METRIC_TOOLTIPS.KEYWORD}>
                      <div className="th-content" onClick={() => handleSort("keyword")}>
                        Mot-clé
                        <Icon name={getSortIcon("keyword")} size={16} />
                      </div>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip text={METRIC_TOOLTIPS.VOLUME}>
                      <div className="th-content" onClick={() => handleSort("volume")}>
                        Volume
                        <Icon name={getSortIcon("volume")} size={16} />
                      </div>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip text={METRIC_TOOLTIPS.TREND}>
                      <div className="th-content">Tendance</div>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip text="Domaine concerné par la ligne. Le client est marqué d'un badge.">
                      <div className="th-content" onClick={() => handleSort("domain")}>
                        Domaine
                        <Icon name={getSortIcon("domain")} size={16} />
                      </div>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip text={METRIC_TOOLTIPS.POSITION}>
                      <div className="th-content" onClick={() => handleSort("position")}>
                        Position
                        <Icon name={getSortIcon("position")} size={16} />
                      </div>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip text={METRIC_TOOLTIPS.URL}>
                      <div className="th-content">URL</div>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip text={METRIC_TOOLTIPS.KEYWORD_TRAFFIC}>
                      <div className="th-content" onClick={() => handleSort("estimatedTraffic")}>
                        Trafic estimé
                        <Icon name={getSortIcon("estimatedTraffic")} size={16} />
                      </div>
                    </Tooltip>
                  </th>
                  <th>
                    <Tooltip text="Types de résultats SERP présents pour ce mot-clé (PAA, Shopping, Images, Vidéos, Featured Snippet, etc.)">
                      <div className="th-content">SERP features</div>
                    </Tooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row) => {
                  const hasPosition = row.position && row.position <= 100;
                  const cleanedUrl = row.url ? cleanUrl(row.url) : null;
                  const truncatedUrl = cleanedUrl && cleanedUrl.length > 60
                    ? cleanedUrl.substring(0, 60) + "..."
                    : cleanedUrl;
                  return (
                    <tr key={row.rowKey} className={row.isClient ? "is-client-row" : ""}>
                      {hasCategories && (
                        <td className="category-cell">
                          {row.category ? (
                            <span className="category-badge">{row.category}</span>
                          ) : (
                            <span className="category-none">-</span>
                          )}
                        </td>
                      )}
                      {hasLocations && (
                        <td className="location-cell">
                          {row.formattedAddress ? (
                            <Tooltip text={row.formattedAddress}>
                              <span className="location-badge">
                                {truncateAddress(row.formattedAddress)}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="location-none">National</span>
                          )}
                        </td>
                      )}
                      <td className="keyword-cell">{row.keyword}</td>
                      <td className="volume-cell">{formatNumber(row.volume)}</td>
                      <td className="trend-cell">
                        {row.historicalMetrics && row.historicalMetrics.length > 0 ? (
                          <TrendSparkline data={row.historicalMetrics} />
                        ) : (
                          <span className="trend-sparkline-empty">—</span>
                        )}
                      </td>
                      <td className="domain-cell">
                        <span className={row.isClient ? "domain-name is-client" : "domain-name"}>
                          {row.domain}
                        </span>
                        {row.isClient && <span className="client-badge-table">CLIENT</span>}
                      </td>
                      <td className="position-cell">
                        {hasPosition ? (
                          <span
                            className="position-badge"
                            style={{ color: getPositionColor(row.position) }}
                          >
                            #{row.position}
                          </span>
                        ) : (
                          <span className="position-none">-</span>
                        )}
                      </td>
                      <td className="url-cell">
                        {cleanedUrl ? (
                          <a
                            href={cleanedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="url-link"
                            title={cleanedUrl}
                          >
                            {truncatedUrl}
                          </a>
                        ) : (
                          <span className="url-none">-</span>
                        )}
                      </td>
                      <td className="traffic-cell">
                        {row.estimatedTraffic === null || row.estimatedTraffic === undefined
                          ? <span className="traffic-none">-</span>
                          : formatNumber(row.estimatedTraffic)}
                      </td>
                      <td className="serp-features-cell">
                        <SerpFeatureIcons features={row.serpFeatures || []} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                <Icon name="ChevronLeft" size={16} />
              </button>
              <span className="pagination-info">
                Page {currentPage} sur {totalPages} ({sortedRows.length} lignes)
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                <Icon name="ChevronRight" size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default KeywordInsights;
